/**
 * Verlanglijst en cadeaulijst, gedeeld door de productkaart en de productpagina.
 *
 * Een knop draagt `data-wishlist="<handle>"` en optioneel `data-wishlist-list="gift"` voor de
 * cadeaulijst. De staat staat in localStorage, dus zonder account overleeft hij een refresh en
 * geldt hij voor het hele apparaat. Clicks lopen via delegatie op document, zodat kaarten die
 * later binnenkomen (filteren, pagineren, quick add) meteen werken.
 *
 * The lists page (assets/gift-lists.js) imports the same instance via `@theme/wishlist` (the
 * importmap points at the identical asset URL, so the module runs once) and listens for
 * `wishlist:change` on document to re-render when a list changes.
 */

const STORES = {
  wishlist: 'lazy-lama:wishlist',
  gift: 'lazy-lama:cadeaulijst',
};

/** Fired on document after any list change, detail: { list }. */
export const CHANGE_EVENT = 'wishlist:change';

/**
 * Per lijst het aria-label voor beide standen, en voor knoppen met zichtbare tekst
 * (`[data-wishlist-text]`) ook het opschrift.
 */
const LABELS = {
  wishlist: {
    on: 'Van je verlanglijst halen',
    off: 'Op je verlanglijst zetten',
  },
  gift: {
    on: 'Staat op je cadeaulijst, klik om te verwijderen',
    off: 'Zet op cadeaulijst',
    onText: 'Op je cadeaulijst',
    offText: 'Zet op cadeaulijst',
  },
};

const listOf = (button) => (button.dataset.wishlistList === 'gift' ? 'gift' : 'wishlist');

/**
 * @param {string} list
 * @returns {string[]} De opgeslagen handles, leeg als de opslag geblokkeerd of bedorven is.
 */
export const read = (list) => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORES[list]) ?? '[]');
    return Array.isArray(stored) ? stored.filter((entry) => typeof entry === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Stores the handles (deduplicated, order kept) and tells the page, so buttons and the lists
 * page follow without a reload.
 *
 * @param {'wishlist' | 'gift'} list
 * @param {string[]} handles
 */
export const write = (list, handles) => {
  try {
    localStorage.setItem(STORES[list], JSON.stringify([...new Set(handles)]));
  } catch {
    // Geblokkeerde opslag mag de knop niet breken: binnen deze pagina klopt de staat nog.
  }

  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
};

const paint = (button, saved) => {
  const labels = LABELS[listOf(button)];

  button.setAttribute('aria-pressed', String(saved));
  button.setAttribute('aria-label', saved ? labels.on : labels.off);

  const text = button.querySelector('[data-wishlist-text]');
  if (text && labels.onText) text.textContent = saved ? labels.onText : labels.offText;
};

/** Zet elke knop in de DOM op de staat die in de opslag staat. */
const sync = () => {
  const cached = {};

  document.querySelectorAll('[data-wishlist]').forEach((button) => {
    const list = listOf(button);
    cached[list] ??= read(list);
    paint(button, cached[list].includes(button.dataset.wishlist));
  });
};

document.addEventListener('click', (event) => {
  const button = event.target.closest?.('[data-wishlist]');
  if (!button) return;

  const list = listOf(button);
  const handle = button.dataset.wishlist;
  const wanted = button.getAttribute('aria-pressed') !== 'true';
  const next = read(list).filter((entry) => entry !== handle);

  if (wanted) next.push(handle);
  write(list, next);

  // Hetzelfde product kan meerdere keren op de pagina staan, bijvoorbeeld de productpagina plus
  // een kaart in "Uit dezelfde collectie": die knoppen tonen samen dezelfde staat.
  document.querySelectorAll(`[data-wishlist="${CSS.escape(handle)}"]`).forEach((other) => {
    if (listOf(other) === list) paint(other, wanted);
  });
});

// Kaarten die na het laden binnenkomen, krijgen hun staat zodra ze in de DOM staan. `paint` raakt
// alleen attributen aan, dus deze observer op childList kan zichzelf niet opnieuw aanroepen.
new MutationObserver((records) => {
  const arrived = records.some((record) =>
    Array.from(record.addedNodes).some(
      (node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches('[data-wishlist]') || node.querySelector('[data-wishlist]'))
    )
  );

  if (arrived) sync();
}).observe(document.documentElement, { childList: true, subtree: true });

// A change in another tab (e.g. a heart on a product page while the lists page is open) reaches
// this tab as a storage event: repaint the buttons and let the lists page re-render.
window.addEventListener('storage', (event) => {
  const list = Object.keys(STORES).find((key) => STORES[key] === event.key);
  if (!list) return;

  sync();
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list } }));
});

sync();
